import { Loader2 } from 'lucide-react';
import React from 'react';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import { APP_ROUTES } from './config/routes';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { QueryClientProvider } from '@tanstack/react-query';
import { AuthErrorBoundary, AdminAuthErrorBoundary } from './components/AuthErrorBoundary';
import { ErrorBoundary } from './components/ErrorBoundary';
import { QueryErrorBoundary } from './components/QueryErrorBoundary';
import { ComponentErrorBoundary } from './components/ComponentErrorBoundary';
import Layout from './components/Layout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { RequireMFAEnrolled } from './components/auth/RequireMFAEnrolled';
import { AuthProvider } from './context/AuthContext';
import { useAuthContext } from './hooks/useAuthContext';
import { useCurrentOrg } from './hooks/useCurrentOrg';
// UserProvider removed - context was unused
import { ThemeProvider } from './context/ThemeContext';
// Feature flag components removed - secure auth is now the default
import { userQueryClient, adminQueryClient } from './lib/query/client';
import { SidebarProvider } from './context/SidebarContext';
import { GlobalLoadingIndicator } from './components/GlobalLoadingIndicator';
import { QuotaExceededProvider } from './components/subscription';
import { PreferencesProvider } from './context/PreferencesContext';

// Import auth pages directly (needed early in app lifecycle)
import Login from './pages/Login';
import Signup from './pages/Signup';
import VerifyEmail from './pages/VerifyEmail';

// Lazy load other pages for better code splitting
const AcceptInvitation = React.lazy(() => import('./pages/AcceptInvitation'));
const OrganizationsPage = React.lazy(() => import('./components/organizations/OrganizationsPage'));
const OnboardingWizard = React.lazy(() => import('./pages/onboarding/OnboardingWizard'));
const SetupMFA = React.lazy(() => import('./pages/SetupMFA'));
const MFAWasReset = React.lazy(() => import('./pages/MFAWasReset'));
const CreateOrganization = React.lazy(() => import('./pages/CreateOrganization'));
const PostLoginRouter = React.lazy(() => import('./pages/PostLoginRouter'));
const SelectOrganization = React.lazy(() => import('./pages/SelectOrganization'));
const SubscriptionExpired = React.lazy(() => import('./pages/SubscriptionExpired'));

// Settings pages
const ProfileSettings = React.lazy(() => import('./pages/settings/ProfileSettings'));
const OrganizationSettingsNew = React.lazy(() => import('./pages/settings/OrganizationSettings'));
const TeamSettings = React.lazy(() => import('./pages/settings/TeamSettings'));
const BillingSettings = React.lazy(() => import('./pages/settings/BillingSettings'));
const WebhookSettings = React.lazy(() => import('./pages/settings/WebhookSettings'));

// Billing callback pages
const BillingSuccess = React.lazy(() => import('./pages/billing/BillingSuccess'));
const BillingCancel = React.lazy(() => import('./pages/billing/BillingCancel'));

// Task guide system
import { TaskGuideProvider } from './context/TaskGuideContext';
import { RequireOrg } from './components/RequireOrg';
import { RequireSubscription } from './components/RequireSubscription';
import { canAccessBilling } from './lib/permissions/billing';

// Admin auth context and layout (keep these as they're lightweight)
import { AdminAuthProvider, useAdminAuth } from './context/AdminAuthContext';
import AdminLayout from './components/AdminLayout';
import { DirectiveHandler } from './components/DirectiveHandler';
import { mergeDirectiveSources } from './lib/directives';
import { userSession, adminSession } from './lib/session';
import { userApiClient, adminApiClient } from './lib/api/client';

// Lazy load ALL pages (both user and admin) for optimal code splitting
// Each page is now a separate chunk, loaded only when navigating to that route
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Workflows = React.lazy(() => import('./pages/Workflows'));
const SetPassword = React.lazy(() => import('./pages/SetPassword'));
const ResetPasswordRequest = React.lazy(() => import('./pages/ResetPasswordRequest'));
const WorkflowEditor = React.lazy(() => import('./components/WorkflowEditor'));
const Components = React.lazy(() => import('./pages/TaskLibrary'));
const ComponentEditor = React.lazy(() => import('./pages/TaskTemplateEditor'));
const Marketplace = React.lazy(() => import('./pages/Marketplace'));
const MarketplaceItemDetail = React.lazy(() => import('./pages/MarketplaceItemDetail'));
const ManageClientOrg = React.lazy(() => import('./pages/ManageClientOrg'));
const Storage = React.lazy(() => import('./pages/Storage'));
const TaskEditor = React.lazy(() => import('./components/TaskEditor/TaskEditor'));
const Deployments = React.lazy(() => import('./pages/Deployments'));

// Marketing pages (public)
const MarketingLayout = React.lazy(() => import('./components/marketing/MarketingLayout'));
const MarketingHome = React.lazy(() => import('./pages/marketing/Home'));
const MarketingFeatures = React.lazy(() => import('./pages/marketing/Features'));
const MarketingPricing = React.lazy(() => import('./pages/marketing/Pricing'));
const MarketingAbout = React.lazy(() => import('./pages/marketing/About'));
const MarketingContact = React.lazy(() => import('./pages/marketing/Contact'));
const MarketingTerms = React.lazy(() => import('./pages/marketing/TermsOfService'));
const MarketingPrivacy = React.lazy(() => import('./pages/marketing/PrivacyPolicy'));
const DocsLayout = React.lazy(() => import('./pages/docs/DocsLayout'));
const DocsIndex = React.lazy(() => import('./pages/docs/DocsIndex'));
const DocPage = React.lazy(() => import('./pages/docs/DocPage'));

// Lazy load admin pages
const AdminLogin = React.lazy(() => import('./pages/AdminLogin'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const AdminUsers = React.lazy(() => import('./pages/AdminUsers'));
const AdminOrganizations = React.lazy(() => import('./pages/AdminOrganizations'));
const AdminInvitations = React.lazy(() => import('./pages/AdminInvitations'));
const AdminRoles = React.lazy(() => import('./pages/AdminRoles'));
const AdminSettings = React.lazy(() => import('./pages/AdminSettings'));
const AdminSubscriptions = React.lazy(() => import('./pages/AdminSubscriptions'));
const MarketplaceAdmin = React.lazy(() => import('./pages/admin/MarketplaceAdmin'));
const DriverPackAdmin = React.lazy(() => import('./pages/admin/DriverPackAdmin'));
const StripeWebhookLogs = React.lazy(() => import('./pages/admin/StripeWebhookLogs'));
const EventsViewer = React.lazy(() => import('./pages/admin/EventsViewer'));
const AdminSystemInfo = React.lazy(() => import('./pages/AdminSystemInfo'));
const AdminSystemLists = React.lazy(() => import('./pages/AdminSystemLists'));
const AdminPartners = React.lazy(() => import('./pages/AdminPartners'));
const AdminPartnerDetail = React.lazy(() => import('./pages/AdminPartnerDetail'));

// Module-scope so the subscribe fn identity is stable across renders —
// React's useEffect dep array on DirectiveHandler's internal subscribe
// effect will only fire once instead of on every parent render.
const userDirectiveSubscribe = mergeDirectiveSources(
  userSession.onDirective.bind(userSession),
  userApiClient.onDirective.bind(userApiClient),
);

const adminDirectiveSubscribe = mergeDirectiveSources(
  adminSession.onDirective.bind(adminSession),
  adminApiClient.onDirective.bind(adminApiClient),
);

const SuspenseFallback = () => (
  <div className="flex items-center justify-center h-screen">
    <Loader2 className="h-8 w-8 animate-spin text-zinc-500" />
  </div>
);

/**
 * Route guard component for the billing page.
 * Restricts access to users with billing permissions.
 */
function BillingRouteGuard() {
  const { user } = useAuthContext();
  const userPermissions = user?.permissions || [];

  if (!canAccessBilling(userPermissions)) {
    return <Navigate to={APP_ROUTES.SUBSCRIPTION_EXPIRED} replace />;
  }

  return <BillingSettings />;
}

/**
 * Keys the Layout on the current org ID so the entire sidebar/content tree
 * reinitializes on org switch — same clean-slate behavior as a fresh login.
 * Without this, the sidebar stays mounted and evaluates nav rules against
 * intermediate state where org/permissions/subscription are out of sync.
 */
function OrgKeyedLayout() {
  const { currentOrg } = useCurrentOrg();
  return <Layout key={currentOrg?.id ?? 'no-org'} />;
}

function AppRoutes() {
  const { loading: authLoading, isAuthenticated } = useAuthContext();

  if (authLoading) {
    return <SuspenseFallback />;
  }

  return (
    <AuthErrorBoundary>
      {isAuthenticated ? (
        <PreferencesProvider>
          <SidebarProvider>
            <TaskGuideProvider>
                <QuotaExceededProvider>
                    <Routes>
                      {/* PostLoginRouter - decision engine, no layout needed */}
                      <Route
                        path="/welcome"
                        element={
                          <React.Suspense fallback={<SuspenseFallback />}>
                            <ProtectedRoute>
                              <PostLoginRouter />
                            </ProtectedRoute>
                          </React.Suspense>
                        }
                      />

                      {/* Invite acceptance — must also be reachable while
                          authenticated so ActionSurface can show accept /
                          already-member / wrong-user instead of the "*" catch-all
                          bouncing the user to /welcome (CU-868kgp2q0). Same page
                          is registered in the unauthenticated block below. */}
                      <Route
                        path="/accept-invitation"
                        element={
                          <React.Suspense fallback={<SuspenseFallback />}>
                            <AcceptInvitation />
                          </React.Suspense>
                        }
                      />

                      {/* Auth-only routes — no Layout (onboarding wizard) */}
                      <Route
                        path="/onboarding/wizard"
                        element={
                          <React.Suspense fallback={<SuspenseFallback />}>
                            <ProtectedRoute>
                              <OnboardingWizard />
                            </ProtectedRoute>
                          </React.Suspense>
                        }
                      />

                      {/*
                        MFA setup + post-reset landing pages bypass the
                        RequireMFAEnrolled guard (so unenrolled users can
                        actually reach the wizard) but still require auth.
                      */}
                      <Route
                        path={APP_ROUTES.SETUP_MFA}
                        element={
                          <React.Suspense fallback={<SuspenseFallback />}>
                            <ProtectedRoute>
                              <SetupMFA />
                            </ProtectedRoute>
                          </React.Suspense>
                        }
                      />
                      <Route
                        path={APP_ROUTES.MFA_WAS_RESET}
                        element={
                          <React.Suspense fallback={<SuspenseFallback />}>
                            <ProtectedRoute>
                              <MFAWasReset />
                            </ProtectedRoute>
                          </React.Suspense>
                        }
                      />

                      {/* All Layout routes — sidebar visible for authenticated users */}
                      <Route
                        path="/"
                        element={
                          <ProtectedRoute>
                            <RequireMFAEnrolled>
                              <OrgKeyedLayout />
                            </RequireMFAEnrolled>
                          </ProtectedRoute>
                        }
                      >
                        {/* Routes accessible without org context */}
                        <Route
                          path="organizations"
                          element={
                            <React.Suspense fallback={<SuspenseFallback />}>
                              <OrganizationsPage />
                            </React.Suspense>
                          }
                        />
                        <Route
                          path="my-organizations"
                          element={<Navigate to="/organizations" replace />}
                        />
                        <Route
                          path="client-organizations"
                          element={<Navigate to="/organizations?tab=managed" replace />}
                        />
                        <Route
                          path="create-organization"
                          element={
                            <React.Suspense fallback={<SuspenseFallback />}>
                              <CreateOrganization />
                            </React.Suspense>
                          }
                        />
                        <Route
                          path="subscription-expired"
                          element={
                            <React.Suspense fallback={<SuspenseFallback />}>
                              <SubscriptionExpired />
                            </React.Suspense>
                          }
                        />

                        {/* Org-scoped routes — require org context */}
                        <Route element={<RequireOrg />}>
                          {/* Subscription-gated feature routes */}
                          <Route element={<RequireSubscription />}>
                              <Route
                                index
                                element={
                                  <React.Suspense fallback={<SuspenseFallback />}>
                                    <QueryErrorBoundary>
                                      <Dashboard />
                                    </QueryErrorBoundary>
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path="dashboard"
                                element={
                                  <React.Suspense fallback={<SuspenseFallback />}>
                                    <QueryErrorBoundary>
                                      <Dashboard />
                                    </QueryErrorBoundary>
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path="task-library"
                                element={
                                  <React.Suspense fallback={<SuspenseFallback />}>
                                    <Components />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path="task-library/edit/:id"
                                element={
                                  <React.Suspense fallback={<SuspenseFallback />}>
                                    <ComponentErrorBoundary componentName="Component Editor">
                                      <ComponentEditor />
                                    </ComponentErrorBoundary>
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path="task-library/new-task"
                                element={
                                  <React.Suspense fallback={<SuspenseFallback />}>
                                    <TaskEditor />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path="task-library/:id/edit-task"
                                element={
                                  <React.Suspense fallback={<SuspenseFallback />}>
                                    <TaskEditor />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path="marketplace"
                                element={
                                  <React.Suspense fallback={<SuspenseFallback />}>
                                    <Marketplace />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path="marketplace/item/:id"
                                element={
                                  <React.Suspense fallback={<SuspenseFallback />}>
                                    <MarketplaceItemDetail />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path="client-organizations/:id"
                                element={
                                  <React.Suspense fallback={<SuspenseFallback />}>
                                    <ManageClientOrg />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path="storage"
                                element={
                                  <React.Suspense fallback={<SuspenseFallback />}>
                                    <Storage />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path="workflows"
                                element={
                                  <React.Suspense fallback={<SuspenseFallback />}>
                                    <Workflows />
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path="workflows/:id"
                                element={
                                  <React.Suspense fallback={<SuspenseFallback />}>
                                    <ComponentErrorBoundary componentName="Workflow Editor">
                                      <WorkflowEditor />
                                    </ComponentErrorBoundary>
                                  </React.Suspense>
                                }
                              />
                              <Route
                                path="deployments"
                                element={
                                  <React.Suspense fallback={<SuspenseFallback />}>
                                    <Deployments />
                                  </React.Suspense>
                                }
                              />
                            </Route>
                            {/* Settings — org required, no subscription required */}
                            <Route
                              path="settings/profile"
                              element={
                                <React.Suspense fallback={<SuspenseFallback />}>
                                  <ProfileSettings />
                                </React.Suspense>
                              }
                            />
                            <Route
                              path="settings/organization"
                              element={
                                <React.Suspense fallback={<SuspenseFallback />}>
                                  <OrganizationSettingsNew />
                                </React.Suspense>
                              }
                            />
                            <Route
                              path="settings/team"
                              element={
                                <React.Suspense fallback={<SuspenseFallback />}>
                                  <TeamSettings />
                                </React.Suspense>
                              }
                            />
                            <Route
                              path="settings/billing"
                              element={
                                <React.Suspense fallback={<SuspenseFallback />}>
                                  <BillingRouteGuard />
                                </React.Suspense>
                              }
                            />
                            <Route
                              path="settings/webhooks"
                              element={
                                <React.Suspense fallback={<SuspenseFallback />}>
                                  <WebhookSettings />
                                </React.Suspense>
                              }
                            />
                            {/* Legacy routes */}
                            <Route
                              path="organization-settings"
                              element={<Navigate to="/settings/billing" replace />}
                            />
                            <Route
                              path="user-settings"
                              element={<Navigate to="/settings/profile" replace />}
                            />
                            <Route
                              path="management/members"
                              element={<Navigate to="/settings/team" replace />}
                            />
                            <Route
                              path="management/approvals"
                              element={<Navigate to="/settings/team" replace />}
                            />
                            <Route
                              path="user-management"
                              element={<Navigate to="/settings/team" replace />}
                            />
                        </Route>
                      </Route>
                      {/* Public pages accessible even when authenticated */}
                      <Route path="/login" element={<Login />} />
                      <Route
                        path="/reset-password-request"
                        element={
                          <React.Suspense fallback={<SuspenseFallback />}>
                            <ResetPasswordRequest />
                          </React.Suspense>
                        }
                      />
                      <Route
                        path="/set-password"
                        element={
                          <React.Suspense fallback={<SuspenseFallback />}>
                            <SetPassword />
                          </React.Suspense>
                        }
                      />
                      <Route path="*" element={<Navigate to="/welcome" replace />} />
                    </Routes>
                </QuotaExceededProvider>
              </TaskGuideProvider>
          </SidebarProvider>
        </PreferencesProvider>
      ) : (
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route
            path="/accept-invitation"
            element={
              <React.Suspense fallback={<SuspenseFallback />}>
                <AcceptInvitation />
              </React.Suspense>
            }
          />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route
            path="/select-organization"
            element={
              <React.Suspense fallback={<SuspenseFallback />}>
                <SelectOrganization />
              </React.Suspense>
            }
          />
          <Route
            path="/reset-password"
            element={
              <React.Suspense fallback={<SuspenseFallback />}>
                <SetPassword />
              </React.Suspense>
            }
          />
          <Route
            path="/reset-password-request"
            element={
              <React.Suspense fallback={<SuspenseFallback />}>
                <ResetPasswordRequest />
              </React.Suspense>
            }
          />
          <Route
            path="/billing/success"
            element={
              <React.Suspense fallback={<SuspenseFallback />}>
                <BillingSuccess />
              </React.Suspense>
            }
          />
          <Route
            path="/billing/cancel"
            element={
              <React.Suspense fallback={<SuspenseFallback />}>
                <BillingCancel />
              </React.Suspense>
            }
          />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      )}
    </AuthErrorBoundary>
  );
}

function MarketingRoutes() {
  return (
    <Routes>
      <Route element={<React.Suspense fallback={<SuspenseFallback />}><MarketingLayout /></React.Suspense>}>
        <Route index element={<React.Suspense fallback={<SuspenseFallback />}><MarketingHome /></React.Suspense>} />
        <Route path="features" element={<React.Suspense fallback={<SuspenseFallback />}><MarketingFeatures /></React.Suspense>} />
        <Route path="pricing" element={<React.Suspense fallback={<SuspenseFallback />}><MarketingPricing /></React.Suspense>} />
        <Route path="about" element={<React.Suspense fallback={<SuspenseFallback />}><MarketingAbout /></React.Suspense>} />
        <Route path="contact" element={<React.Suspense fallback={<SuspenseFallback />}><MarketingContact /></React.Suspense>} />
        <Route path="terms" element={<React.Suspense fallback={<SuspenseFallback />}><MarketingTerms /></React.Suspense>} />
        <Route path="privacy" element={<React.Suspense fallback={<SuspenseFallback />}><MarketingPrivacy /></React.Suspense>} />
        <Route path="docs" element={<React.Suspense fallback={<SuspenseFallback />}><DocsLayout /></React.Suspense>}>
          <Route index element={<React.Suspense fallback={<SuspenseFallback />}><DocsIndex /></React.Suspense>} />
          <Route path="*" element={<React.Suspense fallback={<SuspenseFallback />}><DocPage /></React.Suspense>} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AdminRoutes() {
  const { loading, isAuthenticated } = useAdminAuth();

  if (loading) {
    return <SuspenseFallback />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Routes>
      <Route path="/" element={<AdminLayout />}>
        <Route index element={<AdminDashboard />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="organizations" element={<AdminOrganizations />} />
        <Route path="subscriptions" element={<AdminSubscriptions />} />
        <Route path="invitations" element={<AdminInvitations />} />
        <Route path="roles" element={<AdminRoles />} />
        <Route path="marketplace" element={<MarketplaceAdmin />} />
        <Route path="driver-packs" element={<DriverPackAdmin />} />
        <Route path="logs/events" element={<EventsViewer />} />
        <Route path="logs/application" element={<Navigate to="/logs/events" replace />} />
        <Route path="logs/audit" element={<Navigate to="/logs/events" replace />} />
        <Route path="logs/stripe-webhooks" element={<StripeWebhookLogs />} />
        <Route path="logs/system" element={<Navigate to="/logs/events" replace />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="system-info" element={<AdminSystemInfo />} />
        <Route path="system-lists" element={<AdminSystemLists />} />
        <Route path="partners" element={<AdminPartners />} />
        <Route path="partners/:agreementId" element={<AdminPartnerDetail />} />
      </Route>
    </Routes>
  );
}

function AdminApp() {
  return (
    <AdminAuthProvider>
      <Routes>
        <Route
          path="login"
          element={
            <React.Suspense fallback={<SuspenseFallback />}>
              <AdminLogin />
            </React.Suspense>
          }
        />
        <Route
          path="reset-password-request"
          element={
            <React.Suspense fallback={<SuspenseFallback />}>
              <ResetPasswordRequest />
            </React.Suspense>
          }
        />
        <Route
          path="set-password"
          element={
            <React.Suspense fallback={<SuspenseFallback />}>
              <SetPassword />
            </React.Suspense>
          }
        />
        <Route
          path="*"
          element={
            <AdminAuthErrorBoundary>
              <AdminRoutes />
            </AdminAuthErrorBoundary>
          }
        />
      </Routes>
    </AdminAuthProvider>
  );
}

function App() {
  const { hostname, pathname: path } = window.location;

  // Detect which app domain to render:
  // Production: subdomain detection (app.*, admin.*)
  // Development: path prefix detection (/app/*, /admin/*)
  const isAdmin = hostname.startsWith('admin.') || path.startsWith('/admin');
  const isApp = hostname.startsWith('app.') || path.startsWith('/app');

  const appBase = import.meta.env.VITE_APP_BASE || '';
  const adminBase = import.meta.env.VITE_ADMIN_BASE || '';

  if (isAdmin) {
    return (
      <ErrorBoundary>
        <QueryClientProvider client={adminQueryClient}>
          <ThemeProvider>
            <GlobalLoadingIndicator />
            <BrowserRouter
              basename={adminBase}
              future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
            >
              <DirectiveHandler subscribe={adminDirectiveSubscribe} />
              <AdminApp />
            </BrowserRouter>
            {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
          </ThemeProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    );
  }

  if (isApp) {
    return (
      <ErrorBoundary>
        <QueryClientProvider client={userQueryClient}>
          <ThemeProvider>
            <GlobalLoadingIndicator />
            <BrowserRouter
              basename={appBase}
              future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
            >
              <DirectiveHandler subscribe={userDirectiveSubscribe} />
              <AuthProvider>
                <AppRoutes />
              </AuthProvider>
            </BrowserRouter>
            {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
          </ThemeProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    );
  }

  // Marketing site — public, no auth
  return (
    <ErrorBoundary>
      <QueryClientProvider client={userQueryClient}>
        <ThemeProvider>
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <MarketingRoutes />
          </BrowserRouter>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
